/**
 * MIRRORS `freeze-v2/initiate/request.js` and `freeze-v2/state/dwsUpload.js`
 * (the three validators/builders) and `freeze-v2/initiate/rows.js` (gateState).
 *
 * COPIED, NOT IMPORTED: the two modules are deliberately independent.
 *
 * Divergence from the originals, all forced by the data: there is no combo, so
 * LEVELS is SKU/Sub-category; the sub-category itself is looked up through
 * `../data/subcats`, a mirror of the freeze catalogue, because a dispute row
 * carries no group of its own; there is no pack catalogue, so deriveProposal
 * returns no packId; the DWS partition becomes a
 * three-way partition over this module's own `courierImages` /
 * `merchantImages` / `hasVideo`; and `int` lives here because disputes-v2's
 * format.js has no integer formatter (freeze's does) and carries a drift-guard
 * test that a new export would have to be added to.
 *
 * Everything here is pure. The drawer holds form state; this module owns what
 * the numbers mean and what is missing.
 */

import {
  SHOT_SLOTS,
  IMAGE_TYPES,
  VIDEO_TYPES,
  MAX_IMAGE_BYTES,
  MAX_VIDEO_BYTES,
  mb,
  ext,
} from './copy'
import {hasEvidence} from '../state/evidenceScore'
import {subcatOf} from '../data/subcats'

export function int(n) {
  return Math.round(Number(n) || 0).toLocaleString('en-IN')
}

/** A gap list a merchant can read aloud: 'a, b and c'. */
export function listWords(items) {
  if (items.length <= 1) return items.join('')
  return items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1]
}

/**
 * Four rows in the frozen dataset carry an empty `sku` but a real `product`.
 * `skuBreakdown` groups on this expression and `SkuIntelligence` keys on it;
 * one body here so the table, the drawer and the coverage filter can never
 * disagree about which shipments belong to a SKU.
 */
export function skuKey(x) {
  return (x && (x.sku || x.product)) || ''
}

/** '30 × 24 × 2.85 cm', or an em dash when any side is missing. Multiplication
 *  sign with spaces, matching the ledger grid's own dims line. */
export function dimsText(d) {
  if (!d || !(d.l > 0) || !(d.b > 0) || !(d.h > 0)) return '—'
  return `${d.l} × ${d.b} × ${d.h} cm`
}

/** 'shipment(s)' is developer shorthand; a merchant should read one or the other. */
export function shipWord(n) {
  return n === 1 ? 'shipment' : 'shipments'
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100
}

/**
 * The freeze module's own tolerance. A merchant who types 1.004 against a
 * computed 1.00 has not proposed a different weight.
 */
export const WEIGHT_TOLERANCE_KG = 0.005

export function withinTolerance(a, b) {
  return Math.abs((Number(a) || 0) - (Number(b) || 0)) <= WEIGHT_TOLERANCE_KG
}

/**
 * What a courier bills: the higher of dead and volumetric. One exported
 * function rather than a `Math.max` at each call site — the drawer derives the
 * chargeable weight it submits from this, `weightFacts` decides which of the
 * two governs from this, and two copies of the same rule is how they drift.
 */
export function chargeableKg(deadKg, volKg) {
  return round2(Math.max(Number(deadKg) || 0, Number(volKg) || 0))
}

export function weightFacts(dead, vol, charge) {
  const d = Number(dead) || 0
  const v = Number(vol) || 0
  const c = Number(charge) || 0

  // `known` is the whole point of this function's contract. An empty field
  // parses to 0, and treating that 0 as a MEASUREMENT is wrong in a way that
  // costs the merchant money: clear the volumetric box mid-edit and
  // `Math.max(0.82, 0)` says dead weight governs at 0.82 kg, so the drawer
  // asserts a governing figure that was never entered and offers to LOWER the
  // chargeable weight to it. Neither claim can be made until both weights
  // exist, so both the chip and the mismatch warning wait for them.
  const known = d > 0 && v > 0
  const higher = chargeableKg(d, v)
  const governs = d === v ? 'both' : d > v ? 'dead' : 'vol'
  return {known, higher, governs, mismatch: known && !withinTolerance(c, higher)}
}

/**
 * Mirrored weighted by `r.ships` exactly as the freeze original is, so the two
 * bodies stay comparable. A dispute row IS one shipment and carries no `ships`
 * field, so `q` is always 1 here and this degenerates to the arithmetic mean —
 * which is what the caller wants and what the caller's copy says it is.
 */
export function weightedAvg(rows, key) {
  let w = 0
  let s = 0
  ;(rows || []).forEach((r) => {
    const q = r.ships || 1
    w += q
    s += (Number(r[key]) || 0) * q
  })
  return w ? round2(s / w) : 0
}

/**
 * The charged-weight histogram for an arbitrary set of shipments, in the SAME
 * shape and the SAME order as skuBreakdown's `weights`: most-frequent first,
 * ties broken by weight ascending. Re-derived here rather than read off the
 * roll-up because the level radio can change which shipments the request
 * covers; when the covered set IS the SKU's own set this reproduces
 * skuBreakdown's array exactly, which is what keeps the drawer and the table
 * from ever quoting different figures.
 */
export function histogramOf(rows) {
  const tally = new Map()
  ;(rows || []).forEach((r) => {
    tally.set(r.appliedWeight, (tally.get(r.appliedWeight) || 0) + 1)
  })
  return Array.from(tally.entries())
    .map(([weight, count]) => ({weight, count}))
    .sort((a, b) => b.count - a.count || a.weight - b.weight)
}

/**
 * The charged-weight distribution as SLABS, lightest first, with the freight
 * each slab is carrying.
 *
 * Couriers bill in 0.5 kg steps, and every `appliedWeight` in this dataset lands
 * exactly on one of those steps (verified: the only values present are 1, 1.5
 * and 2). So a slab IS an observed applied weight, and this deliberately does
 * NOT re-bucket with a ceil to 0.5 — re-bucketing could merge two weights that
 * `histogramOf` keeps apart, and the modal slab would then stop agreeing with
 * the chargeable weight `deriveProposal` seeds. The two must always name the
 * same figure, because the drawer marks one slab as the proposal.
 *
 * `excess` is summed from the rows, so it inherits their indicative-freight
 * caveat (EXCESS_NOTE) rather than adding a second approximation.
 *
 * Ascending by weight, unlike `histogramOf`, which is count-first because
 * `deriveProposal` reads its head as the mode. Sorting here would break that.
 */
export function slabBreakdown(rows) {
  const tally = new Map()
  ;(rows || []).forEach((r) => {
    const at = tally.get(r.appliedWeight) || {weight: r.appliedWeight, count: 0, excess: 0}
    at.count += 1
    at.excess += r.excessAmount || 0
    tally.set(r.appliedWeight, at)
  })
  return Array.from(tally.values()).sort((a, b) => a.weight - b.weight)
}

/**
 * The opening proposal. Chargeable is the MODE of what the covered shipments
 * were actually charged — a re-read of real billing, never a separate estimate
 * — and dead/volumetric come from one real shipment that charged exactly that
 * figure, so the three fields land internally consistent (the higher of the
 * pair IS the chargeable weight) and the mismatch alert stays quiet until the
 * merchant moves one of them.
 *
 * With the default SKU level this returns the identical triple the
 * "Recommended weight corrections" table already recommends: histogramOf
 * reproduces skuBreakdown's `weights` order, so `chargeKg === suggestedWeight`,
 * and `covered` preserves row order, so `sample` is the same first-seen row
 * skuBreakdown took `deadKg`/`volKg` from. The roll-up is the fallback, not the
 * source, so a level change cannot leave the fields describing a set they no
 * longer cover.
 */
export function deriveProposal(sku, covered) {
  const hist = histogramOf(covered)
  const chargeKg = hist.length
    ? hist[0].weight
    : (sku && sku.suggestedWeight != null ? sku.suggestedWeight : (sku && sku.modalWeight) || 0)
  const sample = (covered || []).find((r) => r.appliedWeight === chargeKg) || null
  return {
    deadKg: sample && sample.deadKg != null ? sample.deadKg : ((sku && sku.deadKg) || 0),
    volKg: sample && sample.volKg != null ? sample.volKg : ((sku && sku.volKg) || 0),
    // The COURIER's measured carton, from the same sample shipment the two
    // weights above come from — never the merchant's `declaredDims`, which
    // belongs to the declared-carton card and describes the other side of the
    // argument. Undefined rather than a zero triple when there is no sample, so
    // `dimsText` renders its own em dash instead of '0 × 0 × 0 cm'.
    dims: sample ? sample.dims : (sku && sku.dims),
    chargeKg,
  }
}

export const LEVEL = {SKU: 'SKU', SUBCAT: 'Sub-category'}

/**
 * The freeze module offers SKU / Sub-category / Combo. There is no combo here —
 * nothing in the dispute dataset records one — so these are the two.
 *
 * SKU stays the recommended default, where the freeze module recommends
 * sub-category: this drawer is reached from one row of the SKU table, and the
 * request it submits is keyed on that one code.
 */
export const LEVELS = [
  {v: LEVEL.SKU, l: 'SKU',
    why: 'Covers every disputed shipment carrying this SKU code.'},
  {v: LEVEL.SUBCAT, l: 'Sub-category',
    why: 'Agrees one weight for a group of similar products, rather than one code at a time.'},
]

/**
 * Four rows in the frozen dataset carry an empty `sku`. There is no code to
 * agree a freeze against for those, so SKU level is unavailable and
 * sub-category is the default. Nothing else disables a level.
 */
export function levelDisabled(levelValue, sku) {
  return levelValue === LEVEL.SKU && !(sku && sku.sku)
}

export function levelDisabledReason() {
  return 'These shipments have no SKU code on file, so a freeze can only be agreed at sub-category level.'
}

/**
 * The sub-categories on offer, from `../data/subcats` — a mirror of the freeze
 * catalogue, looked up by SKU code and never by product name.
 *
 * Drawn from the shipments the request is raised over rather than from the whole
 * catalogue: a group this SKU has nothing in would be a freeze about other
 * people's shipments. The SKU's own is always included, so the select can never
 * open on a value it does not list — including when the covered rows have been
 * filtered away underneath it.
 */
export function subcatOptions(sku, skuRows) {
  const set = new Set([subcatOf(sku)])
  ;(skuRows || []).forEach((r) => set.add(subcatOf(r)))
  return Array.from(set).sort()
}

/**
 * How far a sub-category freeze reaches, counted over the shipments in view.
 *
 * A sub-category freeze is agreed for the group, while the weight in the drawer
 * is derived from one SKU's shipments. That difference is real and the merchant
 * is the one who pays for it, so it is counted here rather than described in
 * adjectives — and counted over the rows actually on screen, which is the only
 * population this module can honestly speak for.
 */
export function subcatReach(rows, subcat) {
  const inSub = (rows || []).filter((r) => subcatOf(r) === subcat)
  return {shipments: inSub.length, skus: new Set(inSub.map(skuKey)).size}
}

export const ROW_EVIDENCE = {BLOCKED: 'blocked', WEAK: 'weak', READY: 'ready'}

/**
 * MERCHANT-FIRST, deliberately. Courier scan images arrive whether the merchant
 * acts or not, so a rule that only fired when both sides were missing would
 * almost never fire and would blame the merchant for a gap that is not theirs.
 *
 * BLOCKED — nothing of the merchant's own: no images AND no video.
 * WEAK     — the merchant has evidence but the courier never scanned it, so
 *            there is nothing to check it against.
 * READY    — both sides on file.
 */
export function rowEvidenceState(row) {
  // "The merchant has something of their own" is state/evidenceScore.js's
  // hasEvidence(), not a second reading of merchantImages/hasVideo here —
  // it is the same question the dispute gate asks, and two copies of it
  // would drift.
  if (!hasEvidence(row)) return ROW_EVIDENCE.BLOCKED
  if (!(Number(row && row.courierImages) || 0)) return ROW_EVIDENCE.WEAK
  return ROW_EVIDENCE.READY
}

/**
 * Reports only what is ALREADY on file against a shipment. An in-drawer
 * attachment never moves a shipment out of `blocked` — that is honoured in
 * gapsFor, where one attachment answers for the request as a whole. Evaluated
 * against the covered set, not the SKU's whole set, because the level radio can
 * change what the freeze covers right up to the moment it is submitted.
 */
export function partitionRows(covered) {
  const rows = (covered || []).map((row) => ({row, state: rowEvidenceState(row)}))
  return {
    rows,
    blocked: rows.filter((x) => x.state === ROW_EVIDENCE.BLOCKED),
    weak: rows.filter((x) => x.state === ROW_EVIDENCE.WEAK),
    ready: rows.filter((x) => x.state === ROW_EVIDENCE.READY),
  }
}

/**
 * The merchant's own evidence, stated rather than counted. `merchantImages` is
 * only ever 0 or 2 in this dataset and `hasVideo` is a flag, so a single
 * integer beside them would imply a progress count ("2 of 5") that nothing
 * here supports. Every branch below is a literal reading of two real fields.
 */
export function merchantEvidenceWords(row) {
  const n = Number(row && row.merchantImages) || 0
  const video = !!(row && row.hasVideo)
  if (video && n) return 'Video + ' + int(n) + ' images'
  if (video) return 'Video'
  if (n) return int(n) + (n === 1 ? ' image' : ' images')
  return 'none'
}

/**
 * The same format and size rules the freeze module's Evidence Studio applies,
 * so a clip this drawer accepts is a clip that one would have accepted too.
 *
 * Order matters: no file → silent; wrong type → type error; oversize → size
 * error; zero bytes → empty error. The size check precedes the empty check, so
 * a 0-byte file of the right type reports 'This file is empty.'
 */
function validate(file, types, max, kindWords) {
  if (!file) return {ok: false, message: ''}
  if (!types[file.type]) return {ok: false, message: `This is a ${ext(file.name)} file. Upload ${kindWords}.`}
  if (file.size > max) {
    return {ok: false, message: `${mb(file.size)} is over the ${mb(max)} limit for one ${kindWords.includes('clip') ? 'clip' : 'image'}.`}
  }
  if (!file.size) return {ok: false, message: 'This file is empty.'}
  return {ok: true, message: ''}
}

export function validateImage(file) {
  return validate(file, IMAGE_TYPES, MAX_IMAGE_BYTES, 'a JPG, PNG or WebP image')
}

export function validateVideo(file) {
  return validate(file, VIDEO_TYPES, MAX_VIDEO_BYTES, 'an MP4, MOV or WebM clip')
}

/**
 * Slot order follows SHOT_SLOTS, not the merchant's fill order, so two
 * identical sets recorded in a different sequence read the same afterwards.
 *
 * NO SHIPMENT ID IS RECORDED. That is exactly why gapsFor treats an attachment
 * as request-level: the capture form never asks which shipment was filmed, so
 * attributing it to one would invent a link the data model does not hold.
 */
export function buildEvidenceFiles({video, images}) {
  const held = images || {}
  return {
    video: video ? {name: video.name, url: video.url} : null,
    images: SHOT_SLOTS
      .filter((slot) => held[slot.k])
      .map((slot) => ({slot: slot.k, name: held[slot.k].name, url: held[slot.k].url})),
  }
}

/** Takes the IMMUTABLE {video, images[]} shape buildEvidenceFiles returns —
 *  never the drawer's live mutable `images` state object. One video is enough,
 *  and so is one still. */
export function hasAttachedEvidence(files) {
  if (!files) return false
  return !!files.video || (files.images || []).length > 0
}

export const EVIDENCE_STATE = {OPTIONAL: 'OPTIONAL', REQUIRED: 'REQUIRED', SATISFIED: 'SATISFIED'}

/**
 * The ONE place that states what an in-drawer attachment does and does not
 * cover. The gate itself (gapsFor) clears on one attachment at any shipment
 * count; the honesty owed is in this wording, not in the gate.
 */
export function evidenceScope({coveredCount, blockedCount, attached}) {
  const sample = 'One set of readings, attached to the request as a whole rather than to any one shipment.'

  if (!blockedCount) {
    return {
      state: EVIDENCE_STATE.OPTIONAL,
      chip: 'Does not block submission',
      note: attached && coveredCount > 1
        ? sample + ' Every covered shipment already has evidence of its own; this is added on top of it.'
        : '',
    }
  }
  if (!attached) {
    return {
      state: EVIDENCE_STATE.REQUIRED,
      chip: blockedCount === 1
        ? 'Required: 1 covered shipment has no evidence of yours'
        : 'Required: ' + int(blockedCount) + ' covered shipments have no evidence of yours',
      note: 'One packing video, or one photo, is enough to submit. It is attached to the request as a whole,'
        + ' not to a particular shipment.',
    }
  }
  return {
    state: EVIDENCE_STATE.SATISFIED,
    chip: 'Attached — enough to submit',
    note: coveredCount === 1
      ? sample + ' This request covers one shipment, so it is the evidence for that shipment.'
      : sample + ' It stands as a sample behind the single weight proposed for all ' + int(coveredCount)
        + ' covered shipments — it is not ' + int(coveredCount) + ' shipments each evidenced in their own right.',
  }
}

/** Typed keys, not prose. The old drawer assembled one English sentence and
 *  used it as footer text, tooltip AND aria-label; a copy edit broke all three
 *  at once. */
export const GAP = {
  SUBCAT: 'SUBCAT',
  NO_ROWS: 'NO_ROWS',
  NO_MERCHANT_EVIDENCE: 'NO_MERCHANT_EVIDENCE',
  WEIGHTS: 'WEIGHTS',
  PACK: 'PACK',
  PACK_SPEC: 'PACK_SPEC',
  DECLARATION: 'DECLARATION',
}

export const GAP_ORDER = [
  GAP.SUBCAT, GAP.NO_ROWS, GAP.NO_MERCHANT_EVIDENCE, GAP.WEIGHTS, GAP.PACK, GAP.PACK_SPEC,
  GAP.DECLARATION,
]

/**
 * The packing material's own weight. It used to check the material's length,
 * breadth and height too, until those turned out to be a second copy of the
 * dimensions the merchant already gives with the product weight — one box,
 * measured once. The SIDES are gated through GAP.WEIGHTS, which cannot pass
 * without a volumetric weight, which cannot be derived without them.
 */
export function packSpecComplete(spec) {
  return Number((spec || {}).selfWeightG) > 0
}

/**
 * `blocked` is PER SHIPMENT; `attachedEvidence` is ONE BOOLEAN FOR THE WHOLE
 * REQUEST, and one attachment clears NO_MERCHANT_EVIDENCE for every covered
 * shipment at any count. Three reasons, all inherited from the freeze module
 * and all still true here:
 *  1. A request proposes ONE dead/volumetric/chargeable triple for its whole
 *     covered set; a single set of readings is the evidence for that claim.
 *  2. buildEvidenceFiles records no shipment id, so attributing the capture to
 *     one shipment would invent a link the data model does not hold.
 *  3. 44 of the 120 rows in this dataset carry no merchant evidence at all;
 *     clearing only single-shipment requests would answer the common case with
 *     "raise it N times, film it N times".
 * `declared` stays a required gap regardless — the packing-material declaration
 * is a promise about future shipments and no film substitutes for it.
 */
export function gapsFor({
  level, subcat, covered, blocked, deadKg, volKg, chargeKg, packing, packSpec, declared,
  attachedEvidence,
}) {
  const found = []
  // Only at sub-category level is the group part of what is being agreed. At SKU
  // level the subject is the code, and an unnamed group is not a gap.
  if (level === LEVEL.SUBCAT && !String(subcat || '').trim()) found.push({key: GAP.SUBCAT})
  if (!(covered || []).length) found.push({key: GAP.NO_ROWS})
  if ((blocked || []).length && !attachedEvidence) {
    found.push({
      key: GAP.NO_MERCHANT_EVIDENCE,
      count: blocked.length,
      label: blocked.length === 1 ? blocked[0].row.awb : '',
    })
  }
  if (!(Number(deadKg) > 0) || !(Number(volKg) > 0) || !(Number(chargeKg) > 0)) found.push({key: GAP.WEIGHTS})
  if (!String(packing || '').trim()) found.push({key: GAP.PACK})
  // Every material has a self weight, INCLUDING one that declares no box, and
  // the catalogue's figure is a starting point rather than the answer — the
  // freeze is granted against what actually leaves the dock.
  if (!packSpecComplete(packSpec)) found.push({key: GAP.PACK_SPEC})
  if (!declared) found.push({key: GAP.DECLARATION})
  return GAP_ORDER.map((k) => found.find((g) => g.key === k)).filter(Boolean)
}

/**
 * The footer's blocking line. One sentence whatever is missing.
 *
 * It used to name every outstanding gap, which read well at one and became a
 * wall at four. The fields carry the red asterisk that says which they are, so
 * the footer's job is only to say that something still is. The freeze module's
 * own gapSentence was cut to the identical line for the same reason — the two
 * drawers sit one click apart and a merchant should not meet two dialects of
 * the same refusal.
 *
 * The gap KEYS and their details (`count`, `label`) stay: `gapsFor` still
 * returns a typed list and the submit button gates on its length.
 */
export function gapSentence(gaps) {
  if (!gaps.length) return ''
  return 'Please fill all the required details here.'
}
